/**
 * CoinMarketCap API Provider
 * 需要API密钥，仅支持加密货币
 */

import {
  ApiProvider,
  Asset,
  AssetPrice,
  AssetType,
  TimeRange,
  HistoricalData,
  AssetSearchResult,
  AssetValidationResult,
  RequestConfig
} from '@/types/api';

import { coinmarketcapService } from '../coinmarketcap';
import { createApiError, categorizeHttpError } from '../errors';
import { getApiKey } from '../apiKeyManager';

export class CoinMarketCapProvider implements ApiProvider {
  name = 'coinmarketcap';
  priority = 6; // 较低优先级，因为需要Key
  isHealthy = true;
  lastError?: Date;
  supportedAssetTypes: AssetType[] = ['crypto'];
  
  private readonly timeout = 10000;
  
  /**
   * Get single asset price
   */
  async getAssetPrice(asset: Asset, config?: RequestConfig): Promise<AssetPrice> {
    try {
      const results = await this.getAssetPrices([asset], config);
      if (results.length === 0) {
        throw createApiError(
          'NOT_FOUND_ERROR',
          `Asset not found: ${asset.symbol}`,
          { provider: this.name }
        );
      }
      const result = results[0];
      return {
        ...result,
        id: asset.id,
      };
    } catch (error) {
      throw this.handleError(error);
    }
  }
  
  /**
   * Get multiple asset prices
   */
  async getAssetPrices(assets: Asset[], config?: RequestConfig): Promise<AssetPrice[]> {
    try {
      // Filter to only crypto assets
      const cryptoAssets = assets.filter(asset => asset.type === 'crypto');
      if (cryptoAssets.length === 0) {
        return [];
      }
      
      // Check if API key is configured
      const apiKey = getApiKey('coinmarketcap');
      if (!apiKey) {
        throw createApiError(
          'AUTH_ERROR',
          'CoinMarketCap API key is required',
          { provider: this.name }
        );
      }
      
      coinmarketcapService.setApiKey(apiKey);
      
      const ids = cryptoAssets.map(asset => asset.id);
      
      // Use abort signal if provided
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), config?.timeout || this.timeout);
      
      if (config?.signal) {
        config.signal.addEventListener('abort', () => controller.abort());
      }
      
      try {
        const results = await coinmarketcapService.getCryptoPrice(ids);
        clearTimeout(timeoutId);
        return results;
      } catch (error) {
        clearTimeout(timeoutId);
        throw error;
      }
    } catch (error) {
      throw this.handleError(error);
    }
  }
  
  /**
   * Get historical data
   */
  async getHistoricalData(
    asset: Asset,
    timeRange: TimeRange,
    config?: RequestConfig
  ): Promise<HistoricalData> {
    try {
      if (asset.type !== 'crypto') {
        throw createApiError(
          'VALIDATION_ERROR',
          `CoinMarketCap does not support ${asset.type} assets`,
          { provider: this.name }
        );
      }
      
      const apiKey = getApiKey('coinmarketcap');
      if (!apiKey) {
        throw createApiError(
          'AUTH_ERROR',
          'CoinMarketCap API key is required',
          { provider: this.name }
        );
      }
      
      coinmarketcapService.setApiKey(apiKey);
      
      const days = this.timeRangeToDays(timeRange);
      
      // Use abort signal if provided
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), config?.timeout || this.timeout);
      
      if (config?.signal) {
        config.signal.addEventListener('abort', () => controller.abort());
      }
      
      try {
        const rawData = await coinmarketcapService.getHistoricalData(asset.id, days);
        clearTimeout(timeoutId);
        
        // Transform to unified format
        const historicalData: HistoricalData = {
          asset,
          timeRange,
          data: {
            prices: rawData.prices.map(([timestamp, price]) => ({
              timestamp,
              value: price
            })),
            volumes: rawData.total_volumes.map(([timestamp, volume]) => ({
              timestamp,
              value: volume
            })),
            marketCaps: rawData.market_caps?.map(([timestamp, cap]) => ({
              timestamp,
              value: cap
            }))
          },
          metadata: {
            fetchedAt: new Date(),
            source: this.name,
            interval: this.getInterval(timeRange)
          }
        };
        
        return historicalData;
      } catch (error) {
        clearTimeout(timeoutId);
        throw error;
      }
    } catch (error) {
      throw this.handleError(error);
    }
  }
  
  /**
   * Search assets (limited support)
   */
  async searchAssets(
    query: string,
    type?: AssetType,
    config?: RequestConfig
  ): Promise<AssetSearchResult[]> {
    if (type && type !== 'crypto') {
      return [];
    }
    
    const knownAssets = this.getKnownAssets();
    const lowerQuery = query.toLowerCase();
    
    return knownAssets.filter(asset => 
      asset.symbol.toLowerCase().includes(lowerQuery) ||
      asset.name.toLowerCase().includes(lowerQuery)
    );
  }
  
  /**
   * Validate asset
   */
  async validateAsset(
    asset: Partial<Asset>,
    config?: RequestConfig
  ): Promise<AssetValidationResult> {
    try {
      if (asset.type && asset.type !== 'crypto') {
        return {
          valid: false,
          error: `CoinMarketCap only supports crypto assets, not ${asset.type}`
        };
      }
      
      if (!asset.symbol) {
        return {
          valid: false,
          error: 'Asset symbol is required'
        };
      }
      
      const apiKey = getApiKey('coinmarketcap');
      if (!apiKey) {
        return {
          valid: false,
          error: 'CoinMarketCap API key is required'
        };
      }
      
      // Try to fetch price to validate
      const testAsset: Asset = {
        id: asset.id || asset.symbol.toLowerCase(),
        symbol: asset.symbol,
        name: asset.name || asset.symbol,
        type: 'crypto',
        priceStatus: 'loading',
        displayOrder: 0,
        isActive: true
      };
      
      try {
        await this.getAssetPrice(testAsset, { ...config, timeout: 5000 });
        return {
          valid: true,
          asset: testAsset
        };
      } catch (error) {
        return {
          valid: false,
          error: `Asset validation failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
          suggestions: await this.searchAssets(asset.symbol, 'crypto')
        };
      }
    } catch (error) {
      return {
        valid: false,
        error: error instanceof Error ? error.message : 'Validation error'
      };
    }
  }
  
  /**
   * Test connection
   */
  async testConnection(config?: RequestConfig): Promise<boolean> {
    try {
      const apiKey = getApiKey('coinmarketcap');
      if (!apiKey) {
        return false;
      }
      
      // Test with Bitcoin as it's always available
      const testAsset: Asset = {
        id: 'bitcoin',
        symbol: 'BTC',
        name: 'Bitcoin',
        type: 'crypto',
        priceStatus: 'loading',
        displayOrder: 0,
        isActive: true
      };
      
      await this.getAssetPrice(testAsset, { ...config, timeout: 5000 });
      return true;
    } catch (error) {
      console.error('CoinMarketCap connection test failed:', error);
      return false;
    }
  }
  
  // Private helper methods
  
  private handleError(error: unknown): never {
    if (error instanceof Error) {
      if ('code' in error) {
        throw error;
      }
      
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        throw createApiError(
          'NETWORK_ERROR',
          'Network connection failed',
          { provider: this.name, originalError: error }
        );
      }
      
      if (error.name === 'AbortError' || error.message.includes('timeout')) {
        throw createApiError(
          'TIMEOUT_ERROR',
          'Request timed out',
          { provider: this.name, originalError: error }
        );
      }
      
      if ('response' in error && error.response) {
        const response = error.response as any;
        const code = categorizeHttpError(response.status, response.data?.message);
        throw createApiError(
          code,
          response.data?.message || error.message,
          { provider: this.name, originalError: error }
        );
      }
      
      throw createApiError(
        'UNKNOWN_ERROR',
        error.message,
        { provider: this.name, originalError: error }
      );
    }
    
    throw createApiError(
      'UNKNOWN_ERROR',
      'An unknown error occurred',
      { provider: this.name, originalError: error }
    );
  }
  
  private timeRangeToDays(timeRange: TimeRange): number {
    const rangeToDays: Record<TimeRange, number> = {
      '1D': 1,
      '7D': 7,
      '30D': 30,
      '90D': 90,
      '1Y': 365
    };
    
    return rangeToDays[timeRange] || 7;
  }
  
  private getInterval(timeRange: TimeRange): string {
    const rangeToInterval: Record<TimeRange, string> = {
      '1D': '1h',
      '7D': '4h',
      '30D': '1d',
      '90D': '1d',
      '1Y': '1d'
    };
    
    return rangeToInterval[timeRange] || '1d';
  }
  
  private getKnownAssets(): AssetSearchResult[] {
    return [
      { id: 'bitcoin', symbol: 'BTC', name: 'Bitcoin', type: 'crypto' },
      { id: 'ethereum', symbol: 'ETH', name: 'Ethereum', type: 'crypto' },
      { id: 'binancecoin', symbol: 'BNB', name: 'BNB', type: 'crypto' },
      { id: 'solana', symbol: 'SOL', name: 'Solana', type: 'crypto' },
      { id: 'cardano', symbol: 'ADA', name: 'Cardano', type: 'crypto' },
      { id: 'ripple', symbol: 'XRP', name: 'XRP', type: 'crypto' },
      { id: 'dogecoin', symbol: 'DOGE', name: 'Dogecoin', type: 'crypto' },
      { id: 'polkadot', symbol: 'DOT', name: 'Polkadot', type: 'crypto' },
      { id: 'polygon', symbol: 'MATIC', name: 'Polygon', type: 'crypto' },
      { id: 'avalanche-2', symbol: 'AVAX', name: 'Avalanche', type: 'crypto' },
      { id: 'chainlink', symbol: 'LINK', name: 'Chainlink', type: 'crypto' },
      { id: 'uniswap', symbol: 'UNI', name: 'Uniswap', type: 'crypto' },
      { id: 'litecoin', symbol: 'LTC', name: 'Litecoin', type: 'crypto' },
      { id: 'cosmos', symbol: 'ATOM', name: 'Cosmos', type: 'crypto' }
    ];
  }
}
